"use client";

import { SlidersHorizontal, X } from "lucide-react";
import { useState } from "react";
import { PRICE_RANGES } from "~/lib/price-ranges";
import { getClientAgeRangeBySlug } from "~/lib/client-age-ranges";
import type { TherapyDirectoryFilters } from "~/lib/therapy-directory-filters";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import DirectoryFilterOptions from "./directory-filter-options";

interface DirectoryFilterPanelProps {
  filters: TherapyDirectoryFilters;
  isPending: boolean;
  onUpdate: (updates: Record<string, string | undefined>) => void;
}

function getActiveChips(filters: TherapyDirectoryFilters) {
  const chips: Array<{ key: string; label: string; param: string }> = [];

  if (filters.ageRange) {
    const ageRange = getClientAgeRangeBySlug(filters.ageRange);
    if (ageRange) {
      chips.push({
        key: filters.ageRange,
        param: "age",
        label: ageRange.label.replace("Terapeuti za ", ""),
      });
    }
  }

  if (filters.priceRange) {
    chips.push({
      key: filters.priceRange,
      param: "price",
      label: PRICE_RANGES[filters.priceRange].label.replace("Terapeuti ", ""),
    });
  }

  if (filters.gender) {
    chips.push({
      key: filters.gender,
      param: "gender",
      label: filters.gender === "FEMALE" ? "Žena" : "Muškarac",
    });
  }

  return chips;
}

export default function DirectoryFilterPanel({
  filters,
  isPending,
  onUpdate,
}: DirectoryFilterPanelProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const chips = getActiveChips(filters);
  const activeCount = chips.length;

  const trigger = (
    <span
      className={`inline-flex h-[34px] items-center gap-1.5 rounded-full border px-3 text-sm transition-colors ${
        activeCount
          ? "border-brand bg-background font-medium text-brand"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      <SlidersHorizontal className="size-4" aria-hidden="true" />
      Filteri
      {activeCount > 0 && (
        <span
          data-testid="active-filter-count"
          className="inline-flex size-4 items-center justify-center rounded-full bg-brand-action text-[10px] text-brand-foreground"
        >
          {activeCount}
        </span>
      )}
    </span>
  );

  return (
    <div className="contents md:hidden">
      {activeCount > 0 && (
        <div className="order-1 flex basis-full flex-wrap items-center gap-2">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              aria-label={`Ukloni filter: ${chip.label}`}
              disabled={isPending}
              onClick={() => onUpdate({ [chip.param]: undefined })}
              className="inline-flex h-[30px] items-center gap-1.5 rounded-full border border-brand/40 bg-brand/10 pl-3 pr-2 text-xs font-medium text-brand transition-colors hover:bg-brand/15 disabled:opacity-60"
            >
              {chip.label}
              <X className="size-3.5" aria-hidden="true" />
            </button>
          ))}

          {activeCount > 1 && (
            <button
              type="button"
              disabled={isPending}
              onClick={() =>
                onUpdate({
                  age: undefined,
                  price: undefined,
                  gender: undefined,
                })
              }
              className="h-[30px] rounded-full px-2 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60"
            >
              Obriši filtere
            </button>
          )}
        </div>
      )}

      <div className="order-3">
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <button type="button" aria-label="Filteri">
              {trigger}
            </button>
          </SheetTrigger>
          <SheetContent side="bottom">
            <SheetHeader>
              <SheetTitle>Filteri</SheetTitle>
            </SheetHeader>
            <div className="py-4">
              <DirectoryFilterOptions
                filters={filters}
                isPending={isPending}
                onUpdate={onUpdate}
                onSelect={() => setIsSheetOpen(false)}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
